import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, User, Phone, Building } from 'lucide-react';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import Card from '../components/common/Card';
import { mockCompanies } from '../mock/data';
import { useAppContext } from '../contexts/AppContext';

const GuestRegistration: React.FC = () => {
  const { addGuest } = useAppContext();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    idNumber: '',
    phoneNumber: '',
    company: '',
    contactPerson: '',
    visitReason: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const companyOptions = mockCompanies.map(company => ({
    value: company.name,
    label: company.name
  }));

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    console.log('GuestRegistration - Submitting form data:', formData);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Add guest to global state
    console.log('GuestRegistration - Calling addGuest with:', formData);
    addGuest(formData);
    
    setIsSubmitting(false);
    setIsSuccess(true);
    
    // Auto redirect to approval page after 3 seconds
    setTimeout(() => {
      navigate('/approval');
    }, 3000);
  };

  const canSubmit = formData.fullName && formData.idNumber && formData.phoneNumber && formData.company;

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center animate-fade-in">
          <div className="mb-6">
            <div className="mx-auto w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mb-4">
              <Check className="w-8 h-8 text-success-600" />
            </div>
            <h2 className="text-2xl font-bold text-secondary-900 mb-2">
              Đăng ký thành công!
            </h2>
            <p className="text-secondary-600">
              Thông tin của bạn đã được gửi đến Ban Quản Lý. Vui lòng chờ phê duyệt.
            </p>
          </div>
          <div className="text-sm text-secondary-500">
            Tự động chuyển đến màn hình duyệt đăng ký sau 3 giây...
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">
            Đăng Ký Khách Vãng Lai
          </h1>
          <p className="text-secondary-600">
            Vui lòng điền thông tin để đăng ký vào tòa nhà
          </p>
        </div>

        <Card>
          <div className="animate-slide-up">
            <h2 className="text-xl font-semibold text-secondary-900 mb-6">
              Thông tin đăng ký
            </h2>
            <div className="space-y-4">
              <Input
                label="Họ và tên *"
                placeholder="Nhập họ và tên"
                value={formData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                leftIcon={<User size={20} />}
              />
              <Input
                label="Số CCCD/CMND *"
                placeholder="Nhập số CCCD/CMND"
                value={formData.idNumber}
                onChange={(e) => handleInputChange('idNumber', e.target.value)}
              />
              <Input
                label="Số điện thoại *"
                placeholder="Nhập số điện thoại"
                value={formData.phoneNumber}
                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                leftIcon={<Phone size={20} />}
              />
              <Select
                label="Công ty cần liên hệ *"
                placeholder="Chọn công ty"
                options={companyOptions}
                value={formData.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
              />
              <Input
                label="Người cần gặp (không bắt buộc)"
                placeholder="Nhập tên người cần gặp"
                value={formData.contactPerson}
                onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                leftIcon={<User size={20} />}
              />
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Lý do viếng thăm (không bắt buộc)
                </label>
                <textarea
                  className="block w-full px-3 py-2 border border-secondary-300 rounded-lg shadow-sm placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  rows={3}
                  placeholder="Nhập lý do viếng thăm"
                  value={formData.visitReason}
                  onChange={(e) => handleInputChange('visitReason', e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-center mt-8">
              <Button
                size="lg"
                disabled={!canSubmit}
                onClick={handleSubmit}
                loading={isSubmitting}
                className="px-12"
              >
                {isSubmitting ? 'Đang gửi...' : 'Gửi đăng ký'}
              </Button>
            </div>
          </div>
        </Card>

        {/* Information Note */}
        <Card className="mt-6 bg-blue-50 border-blue-200">
          <div className="flex items-start space-x-3">
            <Building className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-blue-900 mb-1">
                Lưu ý:
              </h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Thông tin đăng ký sẽ được gửi đến Ban Quản Lý để duyệt</li>
                <li>• Sau khi được duyệt, vui lòng đến quầy lễ tân để nhận thẻ</li>
                <li>• Vui lòng mang theo CCCD/CMND khi đến tòa nhà</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default GuestRegistration;
